import React, { useState, useEffect, useCallback } from "react";
import {
    Drawer, Box, Typography, IconButton, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, Tooltip, Divider,
} from "@mui/material";
import { Close as CloseIcon, Refresh as RefreshIcon, ArrowForward, SwapHoriz } from "@mui/icons-material";
import { financialAccountService } from "../../services/financialAccountService";
import { AccountTransfer } from "../../types/accounts";
import { formatCurrencyBDT } from "../../utils/currency";

interface TransferHistoryDrawerProps {
    open: boolean;
    onClose: () => void;
}

// Backend caps a single page at 500 (see GET /accounts/transfers). A small
// business doing even a few transfers a day would take years to reach that,
// so one fetch is treated as "the full history" — the note below only shows
// up in the (currently hypothetical) case where it isn't.
const PAGE_LIMIT = 500;

const StatBlock: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
    <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" fontWeight={700} color={color || "text.primary"}>{value}</Typography>
    </Box>
);

export const TransferHistoryDrawer: React.FC<TransferHistoryDrawerProps> = ({ open, onClose }) => {
    const [transfers, setTransfers] = useState<AccountTransfer[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const list = await financialAccountService.listTransfers(undefined, 0, PAGE_LIMIT);
            setTransfers(list);
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || "Failed to load transfer history");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const totalFees = transfers.reduce((sum, t) => sum + Number(t.fee || 0), 0);
    const totalMoved = transfers.reduce((sum, t) => sum + Number(t.amount), 0);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: "100%", sm: 650, md: 750 },
                    maxWidth: "100%",
                    mt: "64px",
                    height: "calc(100% - 64px)",
                },
            }}
            sx={{ zIndex: 1200 }}
        >
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                            <Box sx={{ bgcolor: "primary.light", color: "primary.dark", p: 0.75, borderRadius: 1.5, display: "flex", flexShrink: 0 }}>
                                <SwapHoriz />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6" noWrap>Transfer History</Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    All money moved between your accounts
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                            <Tooltip title="Refresh">
                                <span>
                                    <IconButton onClick={load} disabled={loading}>
                                        <RefreshIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <IconButton onClick={onClose}><CloseIcon /></IconButton>
                        </Box>
                    </Box>

                    {transfers.length > 0 && (
                        <Box sx={{ display: "flex", gap: 4, mt: 2.5 }}>
                            <StatBlock label="Transfers" value={String(transfers.length)} />
                            <StatBlock label="Total Moved" value={formatCurrencyBDT(totalMoved)} />
                            <StatBlock
                                label="Total Fees Paid"
                                value={formatCurrencyBDT(totalFees)}
                                color={totalFees > 0 ? "error.main" : undefined}
                            />
                        </Box>
                    )}
                    {transfers.length === PAGE_LIMIT && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Showing the {PAGE_LIMIT} most recent transfers — the totals above only cover these.
                        </Alert>
                    )}
                </Box>

                <Box sx={{ flexGrow: 1, overflow: "auto", p: 3 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : transfers.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <Box sx={{ display: "inline-flex", bgcolor: "grey.100", color: "grey.500", p: 1.25, borderRadius: "50%", mb: 1 }}>
                                <SwapHoriz />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                No transfers recorded yet.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Transfer</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell align="right">Fee</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {transfers.map((t) => (
                                        <TableRow key={t.transfer_id}>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                {new Date(t.transfer_date).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <Typography variant="body2">{t.from_account.name}</Typography>
                                                    <ArrowForward sx={{ fontSize: 14, color: "text.disabled" }} />
                                                    <Typography variant="body2">{t.to_account.name}</Typography>
                                                </Box>
                                                {t.notes && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t.notes}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2" fontWeight={600}>
                                                    {formatCurrencyBDT(t.amount)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    color={t.fee ? "error.main" : "text.disabled"}
                                                >
                                                    {t.fee ? formatCurrencyBDT(t.fee) : "—"}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>

                {transfers.length > 0 && (
                    <>
                        <Divider />
                        <Box sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="caption" color="text.secondary">
                                Transfer fees reduce the sending account's balance but are not counted as
                                business expenses — tag a manual expense entry if you want a fee to show up
                                in P&amp;L or cash flow.
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
        </Drawer>
    );
};
